import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

class ApiService {
  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Request interceptor
    this.api.interceptors.request.use(
      (config) => {
        console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`);
        return config;
      },
      (error) => {
        console.error('❌ API Request Error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.api.interceptors.response.use(
      (response) => {
        console.log(`✅ API Response: ${response.config.url}`, response.data);
        return response;
      },
      (error) => {
        console.error('❌ API Response Error:', error.response?.data || error.message);
        return Promise.reject(error);
      }
    );
  }

  // Property Services
  async getPropertyDetails(address) {
    try {
      const response = await this.api.get('/property/details', {
        params: { address }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch property details');
    }
  }

  async getPropertyHistory(address) {
    try {
      const response = await this.api.get('/property/history', {
        params: { address }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch property history');
    }
  }

  async getComplexityScore(address, radius = 1) {
    try {
      const response = await this.api.get('/property/complexity', {
        params: { address, radius }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch complexity score');
    }
  }

  async validateAddress(address) {
    try {
      const response = await this.api.post('/property/validate-address', {
        address
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to validate address');
    }
  }

  // Comparables Services - Using ProMix API format (with retry)
  // returnAll: if true, backend returns ALL comparables for client-side caching/re-sorting
  async getComparables(address, radius = 1, maxComparables = 20, returnAll = false) {
    return this.retryRequest(async () => {
      try {
        const response = await this.api.get('/comparables/radius', {
          params: {
            address,
            radius,
            maxComparables, // Display limit (used if returnAll=false)
            returnAll: returnAll ? 'true' : 'false', // 🆕 Request all for caching
            pubrec: 1,
            respFieldConfigId: 'std_radiusconfig',
            includeSimilarityScore: true,
            MinClosePrice: 60000,
            imagesON: true
          }
        });
        return response.data;
      } catch (error) {
        throw this.handleError(error, 'Failed to fetch comparable properties');
      }
    });
  }

  async getFilteredComparables(address, radius = 1, criteria = {}) {
    try {
      const response = await this.api.post('/comparables/filtered', {
        address,
        radius,
        criteria
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch filtered comparables');
    }
  }

  async getComparablesStats(address, radius = 1) {
    try {
      const response = await this.api.get('/comparables/stats', {
        params: { address, radius }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch comparable statistics');
    }
  }

  // Market Services
  async getMarketSummary(address, radius = 1) {
    try {
      const response = await this.api.get('/market/summary', {
        params: { address, radius }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch market summary');
    }
  }

  async getMarketTrends(address, radius = 1) {
    try {
      const response = await this.api.get('/market/trends', {
        params: { address, radius }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch market trends');
    }
  }

  async getMarketActivity(address, radius = 1) {
    try {
      const response = await this.api.get('/market/activity', {
        params: { address, radius }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch market activity');
    }
  }

  // AI Services (Enhanced)
  async processQuery(query, context = {}, images = null) {
    try {
      const response = await this.api.post('/ai/query', {
        query,
        context,
        images
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to process AI query');
    }
  }

  // NEW: Normalize address using OpenAI
  async normalizeAddress(address) {
    try {
      const response = await this.api.post('/ai/normalize-address', {
        address
      });
      return response.data;
    } catch (error) {
      console.error('Address normalization failed, using original:', error);
      // Return original address if normalization fails
      return {
        success: false,
        normalized: address,
        street: null,
        city: null,
        state: null,
        zip: null,
        isValid: false,
        original: address
      };
    }
  }

  // Area-based property search
  async searchPropertiesByArea(area, originalQuery) {
    try {
      const response = await this.api.post('/property/search-area', {
        area,
        originalQuery
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to search properties by area');
    }
  }

  // NEW: Update subject property details
  async updateSubjectProperty(propertyData) {
    try {
      const response = await this.api.put('/property/subject-property', propertyData);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to update subject property');
    }
  }

  // NEW: Find comparables based on subject property
  async findComparablesBySubject(subjectProperty, searchCriteria = {}) {
    try {
      const response = await this.api.post('/property/comparables-by-subject', {
        subjectProperty,
        searchRadius: searchCriteria.searchRadius || 3,
        maxResults: searchCriteria.maxResults || 20,
        tolerance: searchCriteria.tolerance || {
          bedrooms: 1,
          bathrooms: 0.5,
          sqft: 0.25,
          yearBuilt: 10,
          stories: 0.5
        }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to find comparable properties by subject');
    }
  }

  // NEW: Text-based comparable search
  async findComparablesByText(location, searchText, filters = {}, searchCriteria = {}) {
    try {
      const response = await this.api.post('/property/comparables-by-text', {
        location,
        searchText,
        filters,
        searchRadius: searchCriteria.searchRadius || 5,
        maxResults: searchCriteria.maxResults || 30
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to find comparable properties by text');
    }
  }

  // NEW: Advanced property search with filters
  async searchProperties(searchCriteria) {
    try {
      const response = await this.api.post('/property/search', searchCriteria);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to search properties');
    }
  }

  // NEW: Luxury property search
  async searchLuxuryProperties(location, luxuryCriteria = {}) {
    try {
      const response = await this.api.post('/property/search-luxury', {
        location,
        hasPool: luxuryCriteria.hasPool || true,
        minPrice: luxuryCriteria.minPrice || 500000,
        minSqft: luxuryCriteria.minSqft || 2500,
        luxuryFeatures: luxuryCriteria.luxuryFeatures || [],
        radius: luxuryCriteria.radius || 3
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to search luxury properties');
    }
  }

  // AI COMPARABLE SELECTION (Milestone 1)
  async getAISelectedComparables(subjectProperty, comparables, options = {}) {
    try {
      console.log('🤖 AI Comparable Selection starting...');

      const response = await this.api.post('/ai-comparables/select-best', {
        subjectProperty,
        comparables,
        options: {
          maxResults: options.maxResults || 5,
          includeReasoning: options.includeReasoning !== false, // Default true
          uadCompliant: options.uadCompliant !== false, // Default true
          photoWeight: options.photoWeight || 0.6,
          dataWeight: options.dataWeight || 0.4
        }
      }, {
        timeout: 180000 // 3 minutes for AI processing
      });

      console.log('✅ AI Selection complete:', response.data);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to get AI-selected comparables');
    }
  }

  // AI COMPARABLE SELECTION WITH STREAMING (Real-time progress updates)
  async getAISelectedComparablesStream(subjectProperty, comparables, options = {}, onProgress, abortSignal = null) {
    console.log('🤖 AI Comparable Selection with STREAMING starting...');

    const baseUrl = API_BASE_URL.replace('/api', '');

    // ✅ FIX ISSUE #3: Convert uploaded images to base64 for transmission
    let uploadedImagesBase64 = null;
    if (options.uploadedImages && options.uploadedImages.length > 0) {
      console.log(`📷 Converting ${options.uploadedImages.length} uploaded images to base64...`);
      uploadedImagesBase64 = await Promise.all(
        options.uploadedImages.map(async (img) => {
          const base64 = await this.fileToBase64(img.file);
          return {
            data: base64,
            name: img.name,
            type: img.file.type
          };
        })
      );
    }

    // Return a promise that manages the EventSource
    return new Promise((resolve, reject) => {
      // For SSE POST requests, we need to use fetch with a ReadableStream
      const fetchOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          subjectProperty,
          comparables,
          uploadedImages: uploadedImagesBase64, // ✅ FIX ISSUE #3: Include uploaded images
          options: {
            maxResults: options.maxResults || 5,
            includeReasoning: options.includeReasoning !== false,
            uadCompliant: options.uadCompliant !== false,
            photoWeight: options.photoWeight || 0.6,
            dataWeight: options.dataWeight || 0.4
          }
        })
      };

      // ✅ FIX ISSUE #3: Add abort signal support for Stop button
      if (abortSignal) {
        fetchOptions.signal = abortSignal;
      }

      fetch(`${baseUrl}/api/ai-comparables/select-best-stream`, fetchOptions)
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';

          const processStream = async () => {
            try {
              while (true) {
                const { done, value } = await reader.read();

                if (done) {
                  console.log('🔚 Stream complete');
                  break;
                }

                buffer += decoder.decode(value, { stream: true });

                // Process complete SSE messages
                const lines = buffer.split('\n');
                buffer = lines.pop(); // Keep incomplete line in buffer

                for (const line of lines) {
                  if (line.startsWith('data: ')) {
                    try {
                      const data = JSON.parse(line.slice(6));

                      if (data.type === 'error') {
                        reject(new Error(data.message));
                        return;
                      }

                      if (data.type === 'complete') {
                        console.log('✅ AI Selection complete via stream');
                        resolve(data.result);
                        return;
                      }

                      // Call progress callback for other event types
                      if (onProgress && typeof onProgress === 'function') {
                        onProgress(data);
                      }
                    } catch (parseError) {
                      console.warn('Failed to parse SSE data:', line);
                    }
                  }
                }
              }
            } catch (streamError) {
              reject(streamError);
            }
          };

          processStream();
        })
        .catch(error => {
          // ✅ FIX ISSUE #3: Handle abort errors gracefully
          if (error.name === 'AbortError') {
            console.log('🛑 AI analysis stopped by user');
            reject(new Error('Analysis stopped by user'));
          } else {
            console.error('❌ Stream error:', error);
            reject(error);
          }
        });
    });
  }

  // Analyze subject property photos only (for preview)
  async analyzeSubjectProperty(subjectProperty, imageUrls) {
    try {
      const response = await this.api.post('/ai-comparables/analyze-subject', {
        subjectProperty,
        imageUrls
      }, {
        timeout: 60000 // 1 minute
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to analyze subject property');
    }
  }

  // BRACKETING ANALYSIS (NEW)
  async analyzeBracketing(subjectProperty, comparables) {
    try {
      console.log('📊 Bracketing analysis starting...');

      const response = await this.api.post('/bracketing/analyze', {
        subjectProperty,
        comparables
      }, {
        timeout: 30000 // 30 seconds
      });

      console.log('✅ Bracketing analysis complete:', response.data);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to analyze bracketing');
    }
  }

  // TOTAL EXPORT (Milestone 2)
  async exportToTOTAL(subjectProperty, selectedComparables, uadData = {}) {
    try {
      console.log('📄 Exporting to TOTAL (MISMO XML)...');

      const response = await this.api.post('/total/export', {
        subjectProperty,
        selectedComparables,
        uadData
      }, {
        responseType: 'blob' // For file download
      });

      // Create download link
      const blob = new Blob([response.data], { type: 'application/xml' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Generate filename
      const address = subjectProperty.address || subjectProperty.StreetAddress || 'property';
      const sanitized = address.replace(/[^a-zA-Z0-9]/g, '_');
      const timestamp = new Date().toISOString().split('T')[0];
      link.download = `TOTAL_Export_${sanitized}_${timestamp}.xml`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('✅ TOTAL export downloaded');
      return { success: true };
    } catch (error) {
      throw this.handleError(error, 'Failed to export to TOTAL');
    }
  }

  // NEW: Foreclosure property search
  async searchForeclosureProperties(location, criteria = {}) {
    try {
      const response = await this.api.post('/property/search-foreclosures', {
        location,
        maxPrice: criteria.maxPrice,
        radius: criteria.radius || 10
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to search foreclosure properties');
    }
  }

  // Health check
  async healthCheck() {
    try {
      const response = await axios.get(`${API_BASE_URL.replace('/api', '')}/health`);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Backend service unavailable');
    }
  }

  // ✅ FIX ISSUE #3: Utility to convert File to base64
  fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1]; // Remove data:image/...;base64, prefix
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  // Error handling with better error messages
  handleError(error, defaultMessage) {
    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const serverMessage = error.response.data?.message || defaultMessage;

      // Provide specific error messages based on status code
      switch (status) {
        case 400:
          return new Error(`Invalid request: ${serverMessage}`);
        case 401:
          return new Error('Authentication required. Please check your credentials.');
        case 403:
          return new Error('Access denied. You do not have permission for this action.');
        case 404:
          return new Error('Resource not found. Please check the address or try again.');
        case 408:
          return new Error('Request timeout. The server took too long to respond.');
        case 429:
          return new Error('Too many requests. Please wait a moment and try again.');
        case 500:
          return new Error('Server error. Our team has been notified. Please try again later.');
        case 502:
        case 503:
        case 504:
          return new Error('Service temporarily unavailable. Please try again in a few moments.');
        default:
          return new Error(serverMessage);
      }
    } else if (error.request) {
      // Request was made but no response received
      if (error.code === 'ECONNABORTED') {
        return new Error('Request timeout. Please check your connection and try again.');
      }
      return new Error('Network error - please check your internet connection and try again.');
    } else {
      // Something else happened
      return new Error(error.message || defaultMessage);
    }
  }

  // Retry logic with exponential backoff
  async retryRequest(requestFn, maxRetries = 3, initialDelay = 1000) {
    let lastError;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await requestFn();
      } catch (error) {
        lastError = error;

        // Don't retry on certain errors
        if (error.response) {
          const status = error.response.status;
          // Don't retry on client errors (4xx except 408, 429)
          if (status >= 400 && status < 500 && status !== 408 && status !== 429) {
            throw this.handleError(error, 'Request failed');
          }
        }

        // If this was the last attempt, throw the error
        if (attempt === maxRetries) {
          throw this.handleError(error, 'Request failed after multiple attempts');
        }

        // Calculate delay with exponential backoff
        const delay = initialDelay * Math.pow(2, attempt);
        console.log(`⏳ Retry attempt ${attempt + 1}/${maxRetries} after ${delay}ms...`);

        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    throw this.handleError(lastError, 'Request failed after multiple attempts');
  }

  // NEW: Smart Adjustments Services
  async calculateSmartAdjustments(subjectProperty, comparables) {
    try {
      const response = await this.api.post('/smart-adjustments/calculate', {
        subjectProperty,
        comparables
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to calculate smart adjustments');
    }
  }

  // NEW: Calculate intelligent adjustments using OpenAI with property price context
  async calculateIntelligentAdjustments(propertyData) {
    try {
      console.log('🧠 Calling intelligent adjustments API with:', {
        subjectPrice: propertyData.subjectProperty.price,
        comparablesCount: propertyData.comparableProperties.length,
        location: propertyData.location
      });

      const response = await this.api.post('/smart-adjustments/intelligent', {
        subjectProperty: propertyData.subjectProperty,
        comparableProperties: propertyData.comparableProperties,
        marketStatistics: propertyData.marketStatistics,
        location: propertyData.location
      });
      return response.data;
    } catch (error) {
      console.error('❌ Intelligent adjustments API failed:', error);
      throw this.handleError(error, 'Failed to calculate intelligent adjustments');
    }
  }

  async generateAdjustmentReport(adjustments, subjectProperty, comparables) {
    try {
      const response = await this.api.post('/smart-adjustments/report', {
        adjustments,
        subjectProperty,
        comparables
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to generate adjustment report');
    }
  }

  async getAdjustmentSuggestions() {
    try {
      const response = await this.api.get('/smart-adjustments/suggestions');
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to get adjustment suggestions');
    }
  }

  // Generic HTTP methods
  async post(url, data, config = {}) {
    try {
      const response = await this.api.post(url, data, config);
      return response;
    } catch (error) {
      throw this.handleError(error, `POST request failed: ${url}`);
    }
  }

  async get(url, config = {}) {
    try {
      const response = await this.api.get(url, config);
      return response;
    } catch (error) {
      throw this.handleError(error, `GET request failed: ${url}`);
    }
  }

  // Utility methods
  formatAddress(street, city, state, zip) {
    const parts = [street, city, `${state} ${zip}`].filter(Boolean);
    return parts.join(', ');
  }

  isValidAddress(address) {
    if (!address || typeof address !== 'string') return false;
    const parts = address.split(',');
    return parts.length >= 2 && parts[0].trim().length > 0;
  }
}

// Export singleton instance
export default new ApiService();